import React, { useCallback, useState, useEffect, useMemo, ChangeEvent } from 'react';
import debounce from 'lodash/debounce';
import JourneyAutocomplete from 'src/common/form/JourneyAutocomplete';
import { ClearbitLogo } from 'src/common/ClearbitLogo';
import { apiOrganizationSmartSearch } from 'src/utils/journeyApi';

import { Node } from 'src/common/interfaces/node.interface';
import { Nullable } from 'src/types/nullable.type';
import { useStepEditorContext } from 'src/store/step-editor';

import {
  CONTENT_SEARCH_OPTION_TYPE_DESTINATION,
  CONTENT_SEARCH_OPTION_TYPE_EXISTING_CONTENT,
  CONTENT_SEARCH_OPTION_TYPE_POPULAR_STARTS,
} from 'src/editor/constants';
import { LibraryContentSmallCard } from 'src/common/components/library/content-card/small';
import { useCurrentOrganization } from 'src/store/organization';
import { IconButton } from 'src/common/icon-button';
import RightStrongIcon from 'src/monet/icons/RightStrongIcon';
import { useDebouncedEffect } from 'src/common/hooks/use-debounced-effect.hook';
import { useDepsMatchingCallbackRef } from 'src/utils/react/deps-matching-callback-ref.hook';
import classNames from 'classnames';
import { useDelayedUnmount } from 'src/common/spotlight/use-delayed-unmount.hook';
import { checkInputForUrl } from 'src/common/helpers/check-input-for-url';
import { DestinationLogo } from 'src/common/DestinationLogo';

interface Props {
  showExisting: boolean;
  onAutocompleteChange: (value: Nullable<Record<string, string | object>>) => void;
  onChange: (value: string) => void;
  onPreviewNode: (node: Nullable<Node>) => void;
}

type SearchState = {
  applicable: boolean;
  searching: boolean;
  options?: Node[];
};

const INITIAL_SEARCH_STATE: SearchState = { applicable: false, searching: false };

export const EditorContentFormAutocompleteInput = ({
  showExisting,
  onAutocompleteChange,
  onChange,
  onPreviewNode,
}: Props) => {
  const { currentOrganization } = useCurrentOrganization();
  const [searchState, setSearchState] = useState<SearchState>(INITIAL_SEARCH_STATE);

  const { searchQuery, setSearchQuery } = useStepEditorContext();

  const onSmartSearchResultsRef = useDepsMatchingCallbackRef(
    (response: any) => {
      setSearchState((searchState: SearchState) => ({
        ...searchState,
        applicable: true,
        options: [
          ...(showExisting ? formatOptions(response.contents, CONTENT_SEARCH_OPTION_TYPE_EXISTING_CONTENT) : []),
          ...formatOptions(response.destinations, CONTENT_SEARCH_OPTION_TYPE_DESTINATION),
        ],
      }));
    },
    [currentOrganization.id, searchQuery]
  );

  const isValidUrl = Boolean(checkInputForUrl(searchQuery));

  useEffect(() => {
    return () => {
      setSearchQuery('');
    };
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      setSearchState(INITIAL_SEARCH_STATE);
      return;
    }
  }, [searchQuery]);

  useDebouncedEffect(
    async () => {
      if (searchQuery && !isValidUrl) {
        try {
          setSearchState((state) => ({ ...state, searching: true }));
          const deps = [currentOrganization.id, searchQuery];
          const response = await apiOrganizationSmartSearch(currentOrganization.id, searchQuery);
          onSmartSearchResultsRef.current && onSmartSearchResultsRef.current(response, deps);
        } catch (err) {
          //
        } finally {
          setSearchState((state) => ({ ...state, searching: false }));
        }
      }
    },
    250,
    [searchQuery, isValidUrl, showExisting, currentOrganization.id]
  );

  const formatOptions = (items: Array<any>, option_type: string) => {
    return items.map((item) => {
      return { ...item, option_type: option_type };
    });
  };

  const processInputValue = () => {
    const url = checkInputForUrl(searchQuery);
    if (url) {
      onChange(url);
    }
  };

  const renderOption = useCallback((option) => {
    if (option.option_type === CONTENT_SEARCH_OPTION_TYPE_POPULAR_STARTS) {
      return (
        <div className='flex items-center space-x-2'>
          {option.renderIcon()}
          <div className='flex flex-1 truncate text-bedrock-black text-bedrock-p'>{option.title}</div>
        </div>
      );
    } else if (option.option_type === CONTENT_SEARCH_OPTION_TYPE_DESTINATION) {
      return (
        <div className='flex items-center space-x-2'>
          <DestinationLogo destination={option} size='large' />
          <div className='flex flex-1 truncate text-bedrock-black text-bedrock-p'>{option.name}</div>
        </div>
      );
    } else if (option.option_type === CONTENT_SEARCH_OPTION_TYPE_EXISTING_CONTENT) {
      return <LibraryContentSmallCard content={option} />;
    } else {
      return 'Default Option';
    }
  }, []);

  const renderGroup = useCallback(
    (group: Record<string, any>) => {
      if (group.group === CONTENT_SEARCH_OPTION_TYPE_DESTINATION) {
        return 'Suggested content types';
      } else if (group.group === CONTENT_SEARCH_OPTION_TYPE_EXISTING_CONTENT) {
        if (searchQuery) {
          return 'Existing content';
        }

        return 'Existing content';
      } else if (group.group === CONTENT_SEARCH_OPTION_TYPE_POPULAR_STARTS) {
        return 'Popular starts';
      }
      return null;
    },
    [searchQuery]
  );

  const onAutocompleteOptionChange = useCallback(
    (_, newValue: Record<string, string>) => {
      if (newValue) {
        onAutocompleteChange({ type: newValue.option_type, value: newValue });
      } else {
        onAutocompleteChange(null);
      }
    },
    [onAutocompleteChange]
  );

  const debouncedHighlightedOptionHandler = useMemo(
    () =>
      debounce(
        (option: Node) => {
          if (option && option.uuid) {
            onPreviewNode(option);
          } else {
            onPreviewNode(null);
          }
        },
        400,
        { leading: true }
      ),
    [onPreviewNode]
  );

  // Stop the invocation of the debounced function after unmounting
  useEffect(() => {
    return () => {
      debouncedHighlightedOptionHandler.cancel();
    };
  }, [debouncedHighlightedOptionHandler]);

  const showEnterButton = Boolean(isValidUrl);
  const { mounted: mountedEnterButton } = useDelayedUnmount(showEnterButton);

  return (
    <div className='flex flex-col'>
      <JourneyAutocomplete
        placeholder='Embed a URL, search for integration or content'
        id='editor-content-input-autocomplete'
        filterOptions={(option: Node[]) => option}
        filterSelectedOptions
        freeSolo={true}
        options={(!isValidUrl && searchState.options) || []}
        onInputChange={(_: ChangeEvent<{}>, newInputValue: string) => {
          setSearchQuery(newInputValue);
        }}
        groupBy={(option: Record<string, any>) => option.option_type}
        renderGroup={renderGroup}
        onPaste={(e: ClipboardEvent) => {
          const pasted = e.clipboardData?.getData('text');
          if (pasted) {
            onChange(pasted);
          }
        }}
        openOnFocus={true}
        autoFocus={true}
        open={true}
        showEmptyOption={
          isValidUrl ||
          (searchState.applicable && !searchState.searching && searchQuery && searchState.options?.length === 0)
        }
        emptyOptionContent={isValidUrl ? 'Hit enter to embed this' : 'No existing content found'}
        onHighlightChange={debouncedHighlightedOptionHandler}
        renderOption={renderOption}
        getOptionLabel={(option: Record<string, any>) => (option ? option.name : '')}
        onChange={onAutocompleteOptionChange}
        textInputProps={{
          trailingAddon: (
            <div
              className={classNames('transition-opacity opacity-0', {
                invisible: !mountedEnterButton,
                visible: mountedEnterButton,
                'opacity-100': showEnterButton,
              })}
            >
              <IconButton
                onClick={() => {
                  processInputValue();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    processInputValue();
                    e.stopPropagation();
                  }
                }}
              >
                <RightStrongIcon className='text-bedrock-gray-medium group-hover:text-bedrock-brand-secondary group-focus:text-bedrock-brand-secondary group-focus-visible:text-bedrock-brand-secondary group-focus-within:text-bedrock-brand-secondary' />
              </IconButton>
            </div>
          ),
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
              processInputValue();
              e.stopPropagation();
            }
          },
        }}
      />
    </div>
  );
};
